/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './player-selection-tabs.stache';
import can from 'can';
import appContext from 'scripts/app-context';
import dtm from 'scripts/services/dtm';

const TAB_SHOTS_AND_STATS = 'tab-shots-and-stats';
const TAB_LATEST = 'tab-latest';
const TAB_BIO = 'tab-bio';
const TAB_HILLS = 'tab-hills';

const EVENT_READY = 'playerSelectionTabsReady';

const inWebView = window.globalSettings.inWebView;
const courseName = window.globalSettings.courseName;

Component.extend({
    tag: 'player-selection-tabs',
    template: template,
    viewModel: {
        isShotAndStats: false,
        isLatest: false,
        isBio: true,
        isHills: true,
        defaultTab: null,
        courseName: courseName,
        isSelected(tabName) {
            return tabName === can.route.attr('section');
        },
    },
    events: {
        inserted(element) {
            this.checkTabs();
            this.awaitRenderTabsContent()
                .then(() => {
                    this._tabsContentRendered = true;
                    this._activeTab = null;
                    this.element = element;
                    this.tabs = element.find(
                        '.s-tabs-navigation .s-tabs-navigation-inner__item',
                    );
                    this.tabOptions = element.find(
                        '.s-tabs-navigation .s-tabs-navigation-select__option',
                    );
                    this.findContentTabs();
                })
                .then(() => $(document).trigger(EVENT_READY));
        },

        '.s-tabs-navigation-inner__item click': function($element) {
            if (
                this._tabsContentRendered &&
                !$element.hasClass('is-selected')
            ) {
                const dataIndex = $element.attr('data-index');
                this._activeTab = dataIndex;
                can.route.attr('section', dataIndex);
            }
        },

        '.s-tabs-navigation-select change': function($element) {
            if (this._tabsContentRendered) {
                const dataIndex = $element.val();
                this._activeTab = dataIndex;
                can.route.attr('section', dataIndex);
            }
        },

        trackInitTab: function(tabIndex) {
            let tabDomElement = this.element.find(
                '.s-tabs-navigation-inner__item[data-index="' + tabIndex + '"]',
            );
            if (tabDomElement) {
                let tabData = tabDomElement.attr('data-analytics-pd-tab-name');
                dtm.trackPlayerDetailTab('playerDetailTab', tabData);
            }
        },

        checkTabs() {
            const body = $(document.body);

            if (
                (!body.hasClass('cq-wcm-edit') &&
                    !body.hasClass('cq-wcm-design') &&
                    !body.hasClass('cq-wcm-preview')) ||
                inWebView
            ) {
                this.viewModel.attr('isShotAndStats', false);
                this.viewModel.attr('isLatest', false);
            }
            this.hideTabIfNeeded('isShotAndStats', TAB_SHOTS_AND_STATS);
            this.hideTabIfNeeded('isLatest', TAB_LATEST);
            this.hideTabIfNeeded('isBio', TAB_BIO);
            this.hideTabIfNeeded('isHills', TAB_HILLS);
        },

        hideTabIfNeeded(attr, tabName) {
            if (this.isTabHidden(tabName)) {
                this.viewModel.attr(attr, false);
            }
        },

        isTabHidden(tabName) {
            return (
                this.viewModel.attr('hiddenTabs') &&
                this.viewModel.attr('hiddenTabs').indexOf(tabName) > -1
            );
        },

        findContentTabs() {
            let searchContent = () => {
                this.contentTabs = this.element
                    .closest('.l-player-detail')
                    .find('.s-tabs-content .s-tabs-content__item');
                if (this.contentTabs && this.contentTabs.length) {
                    this.activeRoute();
                }
            };
            let searchInterval = setInterval(() => {
                if (!this.contentTabs || !this.contentTabs.length) {
                    searchContent();
                } else {
                    clearInterval(searchInterval);
                }
            }, 300);
            searchContent();
        },

        awaitRenderTabsContent: function() {
            const awaiter = can.Deferred();

            if (
                window.CQ &&
                location.search.indexOf('wcmmode=preview') === -1
            ) {
                if (window.CQ.WCM.areEditablesReady()) {
                    awaiter.resolve();
                }
                window.CQ.WCM.addListener('editablesready', () => {
                    awaiter.resolve();
                });
            } else {
                awaiter.resolve();
            }
            return awaiter;
        },

        onTabChange: function(el) {
            if (window.CQ) {
                var tabIndex = el.getAttribute('data-index');
                var selected = el.classList.contains('is-selected');
                window.CQ.WCM.USOPEN.onTabChange(tabIndex, selected);
            }
        },

        activeRoute() {
            const ready = (tabIndex) => {
                if (this._activeTab) {
                    return;
                }
                this._activeTab = tabIndex;
                can.route.attr('section', tabIndex);
                this.trackInitTab(tabIndex);
                this.updateTabs(tabIndex);
            };
            can.route('section=:section', { section: '' });
            can.route.ready();
            can.route.bind('section', (ev, tabIndex) => {
                if (!Boolean(tabIndex)) {
                    return;
                }

                this.updateTabs(tabIndex);
            });

            if (window.CQ) {
                return ready(TAB_BIO);
            }

            const tab = this.findTab();

            if (tab === TAB_BIO || tab === TAB_HILLS) {
                this.checkShotsAndStatsIsAvailable().then((shotsAndStats) => {
                    this.updateTabAvailable(
                        shotsAndStats,
                        this.viewModel.attr('isLatest'),
                    );
                });
                this.checkPlayerLatestIsAvailable().then((playerLatest) => {
                    this.updateTabAvailable(
                        this.viewModel.attr('isShotAndStats'),
                        playerLatest,
                    );
                });
                return ready(tab);
            }
            this.checkShotsAndStatsIsAvailable()
                .then((shotsAndStats) => {
                    this.updateTabAvailable(
                        shotsAndStats,
                        this.viewModel.attr('isLatest'),
                    );
                    if (tab === TAB_SHOTS_AND_STATS) {
                        if (shotsAndStats) {
                            return ready(TAB_SHOTS_AND_STATS);
                        } else {
                            return ready(TAB_BIO);
                        }
                    }
                })
                .fail(() => {
                    return ready(TAB_BIO);
                });

            this.checkPlayerLatestIsAvailable()
                .then((playerLatest) => {
                    this.updateTabAvailable(
                        this.viewModel.attr('isShotAndStats'),
                        playerLatest,
                    );
                    if (tab === TAB_LATEST) {
                        if (playerLatest) {
                            return ready(TAB_LATEST);
                        } else {
                            return ready(TAB_BIO);
                        }
                    }
                })
                .fail(() => {
                    return ready(TAB_BIO);
                });
        },

        updateTabAvailable(shotsAndStats, playerLatest) {
            this.tabs
                .add(this.tabOptions)
                .filter(`[data-index="${TAB_SHOTS_AND_STATS}"]`)
                .toggleClass('has-data', shotsAndStats);
            this.tabs
                .add(this.tabOptions)
                .filter(`[data-index="${TAB_LATEST}"]`)
                .toggleClass('has-data', playerLatest);

            this.viewModel.attr('isShotAndStats', shotsAndStats);
            this.viewModel.attr('isLatest', playerLatest);
        },

        checkPlayerLatestIsAvailable() {
            if (inWebView || this.isTabHidden(TAB_LATEST)) {
                return can.when(false);
            } else {
                return can.when(
                    appContext.model.waitForProperty('playerLatestIsAvailable'),
                );
            }
        },

        checkShotsAndStatsIsAvailable() {
            if (inWebView || this.isTabHidden(TAB_SHOTS_AND_STATS)) {
                return can.when(false);
            } else {
                return can.when(
                    appContext.model.waitForProperty(
                        'shotsAndStatsIsAvailable',
                    ),
                );
            }
        },

        findTab() {
            if (can.route.attr('section')) {
                return can.route.attr('section');
            }
            if (this.viewModel.attr('defaultTab')) {
                return this.viewModel.attr('defaultTab');
            }
            return TAB_SHOTS_AND_STATS;
        },

        updateTabs(tabIndex) {
            this.tabs = this.element.find(
                '.s-tabs-navigation .s-tabs-navigation-inner__item',
            );
            this.tabOptions = this.element.find(
                '.s-tabs-navigation .s-tabs-navigation-select__option',
            );

            this.select = this.element.find('.js-select');
            this.select.val(can.route.attr('section'));

            const changeTab = function(el) {
                const tab = $(el);
                if (tab.attr('data-index') === tabIndex) {
                    tab.addClass('is-selected').attr('aria-current', 'page');
                } else {
                    tab.removeClass('is-selected').removeAttr('aria-current');
                }
            };
            this.tabs.each((i, el) => {
                changeTab(el);
            });
            this.contentTabs.each((i, el) => {
                changeTab(el);
                this.onTabChange(el);
            });
        },
    },
});
